/**
 * عوض کردنِ رمز از پروفایل (WHOSUP-367) — قرینه‌ی `apps/mobile/app/change-password.tsx`.
 *
 * فرقش با `/reset-password`: آن‌جا کسی آمده که رمزش را نمی‌داند و لینکِ
 * ایمیل ثابت کرده صاحبِ حساب است. این‌جا کاربر وارد است، پس رمزِ فعلی را
 * می‌خواهیم — قاعده‌ها و سنجش در `core/lib/changePassword` است.
 *
 * حسابِ گوگل/اپل رمزی ندارد؛ دکمه‌ی پروفایل برایش دیده نمی‌شود، و اگر کسی
 * آدرس را مستقیم باز کند، همین را می‌شنود به‌جای فرمی که هیچ‌وقت کار نمی‌کند.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/core/lib/supabase';
import {
  CHANGE_PASSWORD_MESSAGE, changeMyPassword, changePasswordProblem, hasPasswordLogin,
} from '@/core/lib/changePassword';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { useSession } from '@/core/session';
import { Card, Note, PasswordInput } from '@/ui/kit';

export function ChangePasswordPage() {
  useLocale();
  const { session } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const user = session?.user;
  const canChange = hasPasswordLogin(user);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    const problem = changePasswordProblem(current, next, again);
    if (problem) { setError(t(CHANGE_PASSWORD_MESSAGE[problem])); return; }

    setBusy(true);
    try {
      const r = await changeMyPassword(supabase.auth, user!.email!, current, next);
      if (r === 'wrongCurrent') { setError(t(CHANGE_PASSWORD_MESSAGE.wrongCurrent)); return; }
      setCurrent(''); setNext(''); setAgain('');
      setDone(true);
    } catch (err) {
      reportError('web.changePassword', err);
      setError(t('errorRetry'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="feed">
      <p><Link to="/profile" className="btn ghost">{t('back')}</Link></p>
      <Card>
        <h1>{t('changePassword')}</h1>
        {!canChange ? (
          <Note>{t('changePasswordNoPassword')}</Note>
        ) : (
          <form onSubmit={submit}>
            <PasswordInput autoComplete="current-password"
              placeholder={t('changePasswordCurrent')} value={current} onChange={(v) => { setCurrent(v); setError(null); }} />
            <PasswordInput autoComplete="new-password"
              placeholder={t('authNewPassword')} value={next} onChange={(v) => { setNext(v); setError(null); }} />
            <PasswordInput autoComplete="new-password"
              placeholder={t('authNewPasswordAgain')} value={again} onChange={(v) => { setAgain(v); setError(null); }} />
            {error ? <p className="error">{error}</p> : null}
            {done ? <Note tone="brand">{t('changePasswordDone')}</Note> : null}
            <button type="submit" className="btn fill block" disabled={busy || !current || !next || !again}>
              {busy ? '…' : t('changePassword')}
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
